import { call, put, select } from 'redux-saga/effects'
import { selectAuthData, selectExtraRoutesAuthData } from '@/containers/Login/AuthSelectors'
import { selectPageConfigurationData } from '@/store/PageConfiguration/PageConfigurationSelectors'
import { generateContactParameters } from '@/containers/SubscriptionFormPageNew/SubscriptionForm/Contact/generateContactParameters'
import { showSnackbar, SNACKBAR_TYPES } from '@/shared/tools/notifications/notification'
import {
  selectContactSubscriptionData,
  selectDoorToDoorPartnerChecklist,
} from '@/containers/SubscriptionFormPageNew/SubscriptionForm/SubscriptionFormSelectors'
import {
  addContractToOpenedContracts,
  resetContactDirty,
  updateClientData,
  updateConfig,
} from '@/containers/SubscriptionFormPageNew/SubscriptionForm/SubscriptionFormSlice'
import {
  selectActiveAddress,
  selectConfig,
  selectCurrentAddressData,
  selectPersonalContactSubscriptionData,
} from '../../SubscriptionForm/SubscriptionFormSelectors'
import { callApi } from '@/shared/tools/apiSagaHelper'
import { addClientError, removeClientError } from '../../SubscriptionForm/SubscriptionFormSlice'
import contractDetails from '@/containers/SubscriptionFormPageNew/mocks/contractDetails.json'
import * as Sentry from '@sentry/react'

export const contactModuleActions = {
  CONTACT_MODULE_VERIFY_EMAIL_V2: 'CONTACT_MODULE_VERIFY_EMAIL_V2',
  CONTACT_MODULE_VERIFY_EMAIL_LIGHT_V2: 'CONTACT_MODULE_VERIFY_EMAIL_LIGHT_V2',
  CONTACT_MODULE_VERIFY_SIRET_V2: 'CONTACT_MODULE_VERIFY_SIRET_V2',
  CONTACT_MODULE_VERIFY_SIRET_LIGHT_V2: 'CONTACT_MODULE_VERIFY_SIRET_LIGHT_V2',
  CONTACT_MODULE_REGISTER_TO_NEWSLETTER_V2: 'CONTACT_MODULE_REGISTER_TO_NEWSLETTER_V2',
  CONTACT_MODULE_SAVE_V2: 'CONTACT_MODULE_SAVE_V2',
  CONTACT_ADD_CONTACT_V2: 'CONTACT_ADD_CONTACT_V2',
  CONTACT_DATA_IMPORT_ZOHO_CLIENT_V2: 'CONTACT_DATA_IMPORT_ZOHO_CLIENT_V2',
  CONTACT_MODULE_LOAD_CONTRACT_DATA: 'CONTACT_MODULE_LOAD_CONTRACT_DATA',
  CONTRACT_CREATE_NEW: 'CONTRACT_CREATE_NEW',
  CONTACT_MODULE_VERIFY_ITALY_FISCAL_CODE_V2: 'CONTACT_MODULE_VERIFY_ITALY_FISCAL_CODE_V2',
}

export function* verifyEmail({ email, emailErrorMsg }) {
  const { validateEmailUrl } = yield select(selectExtraRoutesAuthData)
  const fullUrl = `${validateEmailUrl}?value=${encodeURIComponent(email)}`

  // Email input doesn't write to Redux on every keystroke — it only writes
  // here, via this saga's onBlur dispatch. So this saga IS the user-driven
  // write path for `email`. If the new value is identical to what's already
  // in state, skip the whole thing (don't re-validate, don't flip dirty).
  const current = yield select(selectPersonalContactSubscriptionData)
  const normalizedNew = (email ?? '').trim()
  const normalizedCurrent = (current?.email ?? '').trim()
  if (normalizedNew === normalizedCurrent) {
    return
  }

  try {
    // The email write IS the user edit (no upstream input handler writes it).
    // Therefore it must flip the contact dirty flag — no _skipDirty here.
    yield put(updateClientData({ data: { email }, name: 'personal' }))
    // neverBounceResult is derived from the backend validation, not a user
    // edit on its own ─ keep _skipDirty so it doesn't flip dirty by itself.
    yield put(updateClientData({ data: { neverBounceResult: false }, name: 'preferences', _skipDirty: true }))
    yield put(
      updateClientData({
        data: {
          emailIsLoading: true,
          emailSuggestion: null,
          prefilledEmail: {
            status: false,
            message: '',
          },
        },
        name: 'config',
      }),
    )
    yield put(removeClientError('email'))

    const result = yield call(callApi, {
      endpoint: fullUrl,
      showErrorSnackbar: false,
      method: 'GET',
    })

    const emailSuggestion = result.didYouMean || null
    yield put(updateClientData({ data: { emailIsLoading: false, emailSuggestion }, name: 'config' }))

    if (result.error || !result.valid) {
      yield put(addClientError({ key: 'email', message: result.errorMessage ?? emailErrorMsg }))
      return
    }

    if (result.valid) {
      yield put(updateClientData({ data: { neverBounceResult: 'valid' }, name: 'preferences', _skipDirty: true }))
    }
  } catch (error) {
    yield put(updateClientData({ data: { emailIsLoading: false }, name: 'config' }))
    yield put(updateClientData({ data: { neverBounceResult: false }, name: 'preferences', _skipDirty: true }))
    const status = error?.status || 'unknown'
    if (import.meta.env.PROD && status !== 422) {
      Sentry.captureException(new Error('emailValidationError'), { extra: { error } })
    }
  }
}

export function* verifyEmailLight({ email, callbackError, callback }) {
  const { validateEmailUrl } = yield select(selectExtraRoutesAuthData)
  const fullUrl = `${validateEmailUrl}?value=${encodeURIComponent(email)}`

  try {
    const result = yield call(callApi, {
      endpoint: fullUrl,
      showErrorSnackbar: false,
      method: 'GET',
    })

    if (result.valid) {
      yield call(callback, result)
      return
    }
    yield call(callbackError, result)
  } catch (error) {
    yield call(callbackError, error)
    const status = error?.status || 'unknown'
    if (import.meta.env.PROD && status !== 422) {
      Sentry.captureException(new Error('emailValidationLightError'), { extra: { error } })
    }
  }
}

export function* verifySiretLight({ siret, callback, callbackError }) {
  const { validateSiretUrl } = yield select(selectExtraRoutesAuthData)
  const fullUrl = `${validateSiretUrl}?siret=${encodeURIComponent(siret)}`

  try {
    const result = yield call(callApi, {
      endpoint: fullUrl,
      showErrorSnackbar: false,
      method: 'GET',
    })

    if (result.valid) {
      yield call(callback, result)
      return
    }

    yield call(callbackError, result)
  } catch (error) {
    yield call(callbackError, error)
    const status = error?.status || 'unknown'
    if (import.meta.env.PROD && status !== 422) {
      Sentry.captureException(new Error('siretLightValidationError'), { extra: { error } })
    }
  }
}

export function* verifySiret({ siret, siretErrorMsg }) {
  const { validateSiretUrl } = yield select(selectExtraRoutesAuthData)
  const fullUrl = `${validateSiretUrl}?siret=${encodeURIComponent(siret)}`

  try {
    // Saga-driven re-writes / backend-derived fields (apeCode, socialReason).
    // The user's own SIRET keystroke already flipped dirty via the input
    // hook ─ no need to flip it again from the saga.
    yield put(updateClientData({ data: { siret }, name: 'professional', _skipDirty: true }))
    yield put(
      updateClientData({
        data: {
          siretIsLoading: true,
          siretIsValid: false,
        },
        name: 'config',
      }),
    )
    yield put(removeClientError('pro_siret'))

    const result = yield call(callApi, {
      endpoint: fullUrl,
      showErrorSnackbar: false,
      method: 'GET',
    })

    yield put(updateClientData({ data: { siretIsLoading: false }, name: 'config' }))

    if (result.valid) {
      yield put(updateClientData({ data: { siretIsValid: true }, name: 'config' }))
      yield put(
        updateClientData({
          data: {
            apeCode: result.apeCode || '',
            socialReason: result.socialReason || '',
          },
          name: 'professional',
          _skipDirty: true,
        }),
      )
      return
    }

    yield put(addClientError({ key: 'pro_siret', message: result.errorMessage ?? siretErrorMsg }))
    yield put(
      updateClientData({
        data: { apeCode: '', socialReason: '' },
        name: 'professional',
        _skipDirty: true,
      }),
    )
  } catch (error) {
    yield put(updateClientData({ data: { siretIsLoading: false, siretIsValid: false }, name: 'config' }))
    yield put(addClientError({ key: 'pro_siret', message: siretErrorMsg }))
    yield put(
      updateClientData({
        data: {
          apeCode: '',
          socialReason: '',
        },
        name: 'professional',
        _skipDirty: true,
      }),
    )
    const status = error?.status || 'unknown'
    if (import.meta.env.PROD && status !== 422) {
      Sentry.captureException(new Error('siretValidationError'), { extra: { error } })
    }
  }
}

export function* registerToNewsletter({ requestErrorMsg }) {
  const { registerNewsletterUrl } = yield select(selectExtraRoutesAuthData)
  const { firstName, lastName, phone1, email, zohoId } = yield select(selectPersonalContactSubscriptionData)
  const endpoint = registerNewsletterUrl?.replace('*', zohoId)
  const { city, streetNumber, streetName, zipcode, electricityMeter } = yield select(selectCurrentAddressData)
  const addressId = yield select(selectActiveAddress)
  const options = {
    zoho_id: zohoId,
    lastname: lastName ?? '',
    firstname: firstName ?? '',
    email: email ?? '',
    phone1: phone1 ?? '',
    city: city ?? '',
    street_number: streetNumber ?? '',
    street_name: streetName ?? '',
    zipcode: zipcode ?? '',
    addressId,
    meter_id_elec: electricityMeter?.pdlNo ?? '',
  }

  try {
    yield put(
      updateClientData({
        data: {
          newsletterLoading: true,
          newsletterData: {},
          newsletterError: '',
        },
        name: 'config',
      }),
    )

    yield call(callApi, {
      endpoint,
      showErrorSnackbar: false,
      method: 'POST',
      payload: options,
    })
  } catch (error) {
    yield put(
      updateClientData({
        data: { newsletterLoading: false, newsletterError: requestErrorMsg + error },
        name: 'config',
      }),
    )
    const status = error?.status || 'unknown'
    if (import.meta.env.PROD && status !== 422) {
      Sentry.captureException(new Error('newsletterSendingError'), { extra: { error } })
    }
  }
}

export function* saveContact({ enqueue }) {
  const contactData = yield select(selectContactSubscriptionData)
  const { save_route: url, country, id, contact } = yield select(selectPageConfigurationData)
  const { checkedTitles } = yield select(selectDoorToDoorPartnerChecklist)
  const leadSource = contact?.lead_source ?? ''
  const formParameters = generateContactParameters({
    contactData,
    id,
    country,
    action: 'save',
    doorToDoorPartnerChecklistCheckedTitles: checkedTitles,
    doorToDoorPartnerLeadSource: leadSource ?? '',
  })
  const formParametersObject = Object.fromEntries(formParameters.entries())

  try {
    yield put(updateClientData({ data: { errors: {}, isSaving: true }, name: 'config' }))

    const result = yield call(callApi, {
      endpoint: url,
      enqueue,
      showErrorSnackbar: true,
      payload: formParametersObject,
      method: 'POST',
    })
    yield put(updateClientData({ data: { isSaving: false }, name: 'config' }))
    if (formParametersObject.create_reviews_interaction === '1') {
      yield put(
        updateClientData({
          data: { isReviewRegistered: true, createReviewsInteraction: null, reviewLanguage: null },
          name: 'preferences',
        }),
      )
    }
    // API succeeded: clear the dirty flag so the warning icon disappears.
    yield put(resetContactDirty())
    yield call(showSnackbar, enqueue, {
      type: SNACKBAR_TYPES.SUCCESS,
      message: result.message ?? 'Unexpected save response message',
    })
  } catch (error) {
    yield put(updateClientData({ data: { errors: error.validationErrors ?? {}, isSaving: false }, name: 'config' }))
    const status = error?.status || 'unknown'
    if (import.meta.env.PROD && status !== 422) {
      Sentry.captureException(new Error('saveContactDataError'), { extra: { error } })
    }
  }
}

export function* addContact() {
  try {
    yield put(updateClientData({ data: { errors: {} }, name: 'config' }))
    const { loaderRoutes, country } = yield select(selectAuthData)
    const initUrl = loaderRoutes['add-route']

    const searchParams = window.location.search || ''
    const separator = searchParams ? '&' : '?'
    const countryQuery = `country=${country}`
    const url = `${initUrl}${searchParams}${separator}${countryQuery}`

    const result = yield call(callApi, {
      endpoint: url,
      method: 'GET',
    })
    // Backend-generated zohoId, NOT a user edit ─ must not flip the contact
    // dirty flag (otherwise the "unsaved changes" warning would appear right
    // after opening a brand-new contact form).
    yield put(updateClientData({ data: { zohoId: result.id ?? '' }, name: 'personal', _skipDirty: true }))
  } catch (error) {
    const status = error?.status || 'unknown'
    if (import.meta.env.PROD && status !== 422) {
      Sentry.captureException(new Error('Error adding contact'), { extra: { error } })
    }
    yield put(updateClientData({ data: { errors: { error } }, name: 'config' }))
  }
}
export function* importZohoClientData({ contactId, enqueue }) {
  const { importZohoClientDataUrl } = yield select(selectExtraRoutesAuthData)
  try {
    yield put(updateConfig({ isImportingContactFromZoho: true }))
    yield call(callApi, {
      endpoint: importZohoClientDataUrl,
      method: 'POST',
      payload: { contactId },
      enqueue,
      showErrorSnackbar: true,
    })
  } catch (error) {
    const status = error?.status || 'unknown'
    if (import.meta.env.PROD && status !== 422) {
      Sentry.captureException(new Error('Error importing Zoho client data'), { extra: { error } })
    }
  }
}
export function* createNewContract({ enqueue }) {
  try {
    const { createContractSelectedType: type, contactId } = yield select(selectConfig)
    const { createNewContractUrl } = yield select(selectExtraRoutesAuthData)
    const params = { type, contactId }
    const data = yield call(callApi, {
      endpoint: createNewContractUrl,
      enqueue,
      showErrorSnackbar: true,
      payload: params,
      method: 'POST',
    })
    const contract = {
      id: data.id,
      type,
      config: { loadedFromBackend: true, isEditable: true, hasAlreadyVotedFeedback: false },
    }
    yield put(addContractToOpenedContracts(contract))
    yield put(updateConfig({ addContractModal: false, activeTab: data.id + '-' + type }))
  } catch (error) {
    const status = error?.status || 'unknown'
    if (import.meta.env.PROD && status !== 422) {
      Sentry.captureException(new Error('Error creating new contract'), { extra: { error } })
    }
  }
}

const GENDER_CIVILITY = { F: 'mme', M: 'mr' }

export function buildPersonalPayloadFromFiscalCodeData(data) {
  const civility = GENDER_CIVILITY[(data?.gender ?? '').toUpperCase()]

  return {
    ...(civility && { civility }),
    ...(data?.birth_date && { birthdate: data.birth_date }),
    ...(data?.location?.name && { bornCity: data.location.name }),
  }
}

export function* verifyItalyFiscalCode({ fiscalCode }) {
  const { italyFiscalCodeUrl } = yield select(selectExtraRoutesAuthData)
  const code = encodeURIComponent((fiscalCode ?? '').trim())

  yield put(updateClientData({ data: { fiscalCodeIsLoading: true, fiscalCodeIsValid: null }, name: 'config' }))
  yield put(removeClientError('taxIdCode'))

  try {
    const data = yield call(callApi, {
      endpoint: italyFiscalCodeUrl,
      asteriskReplacement: code,
      showErrorSnackbar: false,
      method: 'GET',
    })
    yield put(updateClientData({ data: { fiscalCodeIsLoading: false, fiscalCodeIsValid: true }, name: 'config' }))

    const payload = buildPersonalPayloadFromFiscalCodeData(data)

    if (Object.keys(payload).length > 0) {
      // Backend prefill from Italian fiscal code lookup. The user's fiscal
      // code keystroke already flipped dirty; the derived personal fields
      // should NOT flip it a second time.
      yield put(updateClientData({ data: payload, name: 'personal', _skipDirty: true }))
    }
  } catch (error) {
    const errorMessage = error.responseData?.message ?? 'Invalid fiscal code'
    yield put(updateClientData({ data: { fiscalCodeIsLoading: false, fiscalCodeIsValid: false }, name: 'config' }))
    yield put(addClientError({ key: 'taxIdCode', message: errorMessage }))
  }
}

export function* loadContractData({ contractId, enqueue }) {
  if (import.meta.env.DEV && contractDetails[contractId]) {
    yield put(addContractToOpenedContracts(contractDetails[contractId]))
    return
  }
  const { getContractDataUrl } = yield select(selectExtraRoutesAuthData)

  try {
    const data = yield call(callApi, {
      endpoint: getContractDataUrl,
      asteriskReplacement: contractId,
      enqueue,
      showErrorSnackbar: true,
    })
    yield put(addContractToOpenedContracts(data))
  } catch (error) {
    const status = error?.status || 'unknown'
    if (import.meta.env.PROD && status !== 422) {
      Sentry.captureException(new Error('Error loading contract data'), { extra: { error } })
    }
  }
}
